import type { FrWidgetToken } from '../widget-types'

// The wire form of a declared schema.
//
// Written by the runner when a service is deployed or installed, stored on the definition, and read back by
// the console — which rebuilds real zod from it (see ./decode.ts) and runs the SAME pipeline the runner runs.
// That is the whole point: a form reports exactly the errors an execution would, because both sides validate
// against the same schema rather than against two hand-kept descriptions of it.
//
// Hand-rolled rather than `z.toJSONSchema()`, because JSON Schema drops everything that makes a FlowRunner
// field a FlowRunner field — `.label()`, `.shared()`, `.dictionary()`, `.map()`, an author's own check
// message — and rebuilding zod from it would need a JSON-Schema interpreter anyway.

// A value that survives JSON. The two primitives that do not are boxed (see ./encode.ts).
export type JsonValue =
  | string | number | boolean | null
  | { $bigint: string }
  | { $date: string }
  | JsonValue[]
  | { [key: string]: JsonValue }

/** A constraint the author declared. `min`/`max` cover both length and magnitude — zod spells them alike. */
export interface CheckJSON {
  kind: 'min' | 'max' | 'length' | 'int' | 'multipleOf' | 'regex' | 'startsWith' | 'endsWith' | 'includes'
  value?: JsonValue
  // `.min(3)` is inclusive, `.gt(3)` is not. Absent means inclusive.
  exclusive?: true
  // An author's own message (`z.string().min(3, 'Too short')`), so the console words the failure the way the
  // runtime will. Absent ⇒ the pipeline's own wording applies.
  message?: string
}

/** A serialized regex — `RegExp` itself is not JSON. Indexable so it satisfies `JsonValue`. */
export interface RegexJSON {
  [key: string]: JsonValue
  source: string
  flags: string
}

// The metadata an author attached. Native zod keys drive display; `fr`-prefixed keys are FlowRunner's.
export interface MetaJSON {
  title?: string
  description?: string
  examples?: JsonValue[]
  frShared?: boolean
  frWidget?: FrWidgetToken
  frSecret?: boolean
  frDictionary?: { id: string; dependsOn: string[] }
  frLabels?: Record<string, string>
}

export type FieldType =
  | 'string' | 'number' | 'bigint' | 'boolean' | 'date'
  | 'enum' | 'literal' | 'object' | 'array' | 'record' | 'union'
  /**
   * Anything the encoder does not model — and also where a declared `z.any()` / `z.unknown()` legitimately
   * lands, since "validates nothing" is exactly what those two declare. The `unsupported` breadcrumb is what
   * separates the two cases: a modelling gap carries `['type:…']`, a declared pass-through carries nothing.
   *
   * Decodes to `z.unknown()` either way, so it validates nothing.
   */
  | 'unknown'

export interface FieldJSON {
  name: string
  type: FieldType
  // A zod format token: 'email' | 'url' | 'ipv4' | 'datetime' | 'safeint' | …
  format?: string
  // Accepts `undefined` / `null`. Recorded separately because they are separate modifiers, and the pipeline's
  // absence policy is derived by PROBING the rebuilt schema rather than by reading these back.
  optional?: true
  nullable?: true
  default?: JsonValue
  checks?: CheckJSON[]
  // enum members, or the literal's own value(s).
  values?: JsonValue[]
  meta?: MetaJSON
  // A schema-level message (`z.email('must be an email')`).
  message?: string
  // type === 'object'
  properties?: FieldJSON[]
  // type === 'array'
  element?: FieldJSON
  /**
   * type === 'union' — every declared branch, in declaration order.
   *
   * All of them are carried, not just the first. The first decides what the console DRAWS, but the rebuilt
   * schema has to accept everything the author's does: `json/types.ts`'s one-directional invariant is that a
   * decoded schema is never STRICTER than the author's, and encoding one branch would break it — the form
   * would reject a shape execution accepts.
   */
  options?: FieldJSON[]
  /**
   * type === 'record' — the schema its VALUES are held to. Keys are not modelled: the runtime only ever
   * walks a record's values (see ../schema/coerce.ts), and a decoded `z.record` therefore accepts any string
   * key, which keeps it no stricter than the author's.
   */
  value?: FieldJSON
  /**
   * What could not cross the wire — `['check:overwrite']`, `['wrapper:prefault']`, `['format:xyz']`.
   *
   * Functions cannot be serialized, so `.refine()`, `.transform()`, `.trim()` and friends are dropped. The
   * invariant that follows is deliberate and one-directional: **a decoded schema is never STRICTER than the
   * author's**. The console may therefore accept a value the runtime later rejects, but never reject one the
   * runtime would accept — and the runtime re-validates every invocation, so it stays the authority.
   */
  unsupported?: string[]
}

export interface ParamsSchemaJSON {
  // Bumped only for a breaking change to the shapes above. Stored payloads outlive the code that wrote them:
  // a definition is encoded once at deploy time and decoded by whatever console version is live later.
  v: 1
  fields: FieldJSON[]
}

export const SCHEMA_JSON_VERSION = 1 as const
